import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import { TreeResponseHandlerData } from "@src/components/tree/TreeTypes";

export interface SyllabusAPIResponse {
  statusCode: number;
  httpStatus: string;
  response: {
    content: SyllabusContent[];
    pageable: Pageable;
    totalPages: number;
    totalElements: number;
    last: boolean;
    size: number;
    number: number;
    numberOfElements: number;
    first: boolean;
    empty: boolean;
  };
}

export interface SyllabusContent {
  _id: string;
  id?: string;
  name: string;
  duration: number;
  subjectId: string;
  parentId: string;
  type: string;
  searchKey: string;
  createdOn: string;
  createdByName: string;
  createdByEmail: string;
  _class: string;
  hasChildren: boolean;
}

interface Pageable {
  pageNumber: number;
  pageSize: number;
  offset: number;
  unpaged: boolean;
  paged: boolean;
}

interface TreeResponse extends APIResponse {
  response: {
    content: TreeResponseHandlerData;
    pageable: Pageable;
    totalPages: number;
    totalElements: number;
    last: boolean;
    size: number;
    number: number;
    numberOfElements: number;
    first: boolean;
    empty: boolean;
  };
}

export interface GetBatchSyllabusChildrenResponse extends APIResponse {
  response: TreeResponseHandlerData;
}

export interface UpdateSubjectNameResponse extends APIResponse {
  response: string;
}

export const TreeApiSlice = createApi({
  reducerPath: "treeApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["getSubjectsTree", "getSyllabusTree", "getBatchSyllabusTree"],
  endpoints: (builder) => ({
    getSubjectsTree: builder.query<
      TreeResponse,
      {
        resourceId: string | undefined;
        pageNo: number;
        pageSize: number;
        parentType?: string;
        addExpand?: boolean;
      }
    >({
      query: ({ resourceId, pageNo, pageSize, parentType, addExpand }) => {
        let URL = `tree?resourceId=${resourceId}&pageNo=${pageNo}&pageSize=${pageSize}`;
        if (
          parentType === "topic" ||
          (parentType === "sub topic" && addExpand)
        ) {
          URL = `${URL}&expandRequired=${false}`;
        }

        return {
          url: URL,
          method: "GET",
        };
      },
      providesTags: ["getSubjectsTree"],
    }),
    getSyllabusTree: builder.query<
      SyllabusAPIResponse,
      {
        resourceId?: string;
        syllabusId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ resourceId, syllabusId, pageNo, pageSize }) => ({
        url: `tree/syllabus?resourceId=${resourceId}&syllabusId=${syllabusId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: ["getSyllabusTree"],
    }),
    getSyllabusTreeForBatch: builder.query<
      TreeResponse,
      {
        resourceId: string;
        syllabusId: string | undefined;
        batchId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ resourceId, syllabusId, batchId, pageNo, pageSize }) => ({
        url: `tree/batch-syllabus?resourceId=${resourceId}&syllabusId=${syllabusId}&batchId=${batchId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: ["getBatchSyllabusTree"],
    }),
    getBatchSyllabusChildren: builder.mutation<
      GetBatchSyllabusChildrenResponse,
      { syllabusId: string; batchId: string; payload: string[] }
    >({
      query: ({ syllabusId, batchId, payload }) => ({
        url: `tree/get-batch-syllabus-children`,
        method: "POST",
        params: { syllabusId, batchId },
        body: payload,
      }),
    }),
    updateTreeNodeName: builder.mutation<
      UpdateSubjectNameResponse,
      { params: { name: string; resourceId: string; parentType: string } }
    >({
      query: ({ params }) => ({
        url: `subject/update-name`,
        method: "PATCH",
        params: params,
      }),
    }),
  }),
});

export const {
  useLazyGetSubjectsTreeQuery,
  useLazyGetSyllabusTreeQuery,
  useLazyGetSyllabusTreeForBatchQuery,
  useGetSyllabusTreeForBatchQuery,
  useGetBatchSyllabusChildrenMutation,
  useUpdateTreeNodeNameMutation,
} = TreeApiSlice;
